import React from 'react'
import { useApp } from '../context/AppContext'
import { api } from '../api/client'
import type { TodayPriority, TodayScreen } from '../api/client'
import { screenCache } from '../api/screenCache'
import CardAttentionBanner from '../payments/CardAttentionBanner'
import OverdueBanner from '../billing/OverdueBanner'
import ProLock from '../billing/ProLock'
import { HINT_TODAY } from '../billing/rungs'
import { useBilling } from '../billing/useBilling'
import { useCardAccount } from '../payments/useCardAccount'
import { canTakeCardPayments } from '../payments/visibility'
import { formatPrice } from '../catalog/format'
import { whatsappLink } from '../today/whatsapp'
import { greetingFor } from '../today/greeting'
import { birthdayMessage, birthdayWhen } from '../today/birthdays'
import { readSnoozed, snooze, unsnoozed } from '../today/snooze'
import SetupChecklist from '../today/SetupChecklist'
import { emptyDayLine } from '../today/emptyDay'
import AppointmentRow from '../components/AppointmentRow'
import { formsNeededLine } from '../forms/status'
import './Today.css'
import { Icon } from '../ui/Icon'

function formatMinutes(min: number) {
  const h = Math.floor(min / 60)
  const m = min % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function formatDate(iso: string) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

export default function Today() {
  const [settingUp, setSettingUp] = React.useState(false)
  const { state, dispatch } = useApp()
  const cardShown = canTakeCardPayments(useCardAccount())
  const { coachLocked } = useBilling()
  const cached = screenCache.get<TodayScreen>('today')
  const [screen, setScreen] = React.useState<TodayScreen | null>(cached ?? null)
  const [failed, setFailed] = React.useState(false)
  const [openPriority, setOpenPriority] = React.useState<number | null>(null)
  const [snoozed, setSnoozed] = React.useState<Set<string>>(() => (cached ? readSnoozed(cached.date) : new Set()))
  const latest = React.useRef(0)

  // A request counter, not a per-effect `live` flag: a foreground reload
  // (below) calls this from outside the effect, so a stale response has to
  // be recognised by comparing tokens rather than by an effect's own closure.
  // A cached screen is shown at once; a failed refresh behind it just keeps
  // that view, it never replaces it with the full error screen.
  const load = React.useCallback(() => {
    const request = ++latest.current
    setFailed(false)
    api
      .getToday()
      .then((data) => {
        if (request === latest.current) {
          setScreen(data)
          setSnoozed(readSnoozed(data.date))
          screenCache.set('today', data)
        }
      })
      .catch(() => {
        if (request === latest.current) setFailed(true)
      })
  }, [])

  React.useEffect(() => {
    load()
  }, [load, state.calendarVersion])

  // Closing an appointment sheet opened from here refreshes the day, so a
  // payment or status change shows at once.
  const sheetOpen = state.appointmentId !== null
  const wasOpen = React.useRef(sheetOpen)
  React.useEffect(() => {
    if (wasOpen.current && !sheetOpen) load()
    wasOpen.current = sheetOpen
  }, [sheetOpen, load])

  // Today is the default tab of an iOS home-screen app; if iOS keeps it in
  // memory overnight, reload when it comes back to the foreground so the
  // briefing isn't yesterday's.
  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [load])

  // The tap is the AI acceptance KPI's event (docs/16): reported in the
  // background by the card's key, and nothing here waits on it or shows a
  // failure. An older API sends no key, and then nothing is reported.
  const report = (p: TodayPriority, outcome: 'acted' | 'snoozed') => {
    if (p.key) api.reportPriorityOutcome(p.key, outcome).catch(() => {})
  }

  const openPriorityAction = (p: TodayPriority) => {
    report(p, 'acted')
    if (p.type === 'coach') {
      dispatch({ type: 'SET_ACTIVE_TAB', payload: 'goals' })
      return
    }
    if (p.type === 'review') {
      dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: 'reviews' })
      return
    }
    if (p.client_id) {
      dispatch({ type: 'OPEN_CLIENT', payload: p.client_id })
      dispatch({ type: 'SET_ACTIVE_TAB', payload: 'clients' })
      return
    }
    dispatch({ type: 'SET_ACTIVE_TAB', payload: 'calendar' })
  }

  const snoozePriority = (date: string, p: TodayPriority) => {
    report(p, 'snoozed')
    setSnoozed(snooze(date, p))
    setOpenPriority(null)
  }

  if (!screen) {
    if (failed) {
      return (
        <div className="screen today-screen">
          <div className="today-status" role="alert">
            <p className="today-error">Couldn’t load your day. Check your connection and try again.</p>
            <button type="button" className="btn btn-secondary btn-compact" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      )
    }
    return <div className="screen today-screen" aria-busy="true" />
  }

  const name = state.me?.professional.display_name ?? ''
  const booked = screen.metrics.appointment_count > 0
  const fullyBooked = booked && screen.metrics.free_slots === 0
  const priorities = unsnoozed(screen.priorities, snoozed)
  // Tapping the line opens the first of today's clients whose form is to do.
  const formsLine = formsNeededLine(screen.forms_needed ?? 0)
  const firstNeedingForm = screen.appointments.find((a) => a.form_status === 'needed')

  return (
    <div className="screen today-screen">
      <OverdueBanner />
      <CardAttentionBanner />
      <div className="screen-header">
        <small>{formatDate(screen.date)}</small>
        <h1>
          {screen.greeting || greetingFor(new Date(), state.me?.professional.timezone)},<br />
          {name}
        </h1>
        {/* The briefing prose is Pro, whatever a cached day carries. */}
        {screen.narrative && !coachLocked && <p className="greeting">{screen.narrative}</p>}
        {formsLine &&
          (firstNeedingForm ? (
            <button
              type="button"
              className="today-forms-line"
              onClick={() => dispatch({ type: 'OPEN_APPOINTMENT', payload: firstNeedingForm.id })}
            >
              {formsLine} <Icon name="chevron-right" size={14} />
            </button>
          ) : (
            <p className="today-forms-line">{formsLine}</p>
          ))}
      </div>

      {failed && (
        <div className="today-status" role="alert">
          <p className="today-error">Couldn’t load your day. Check your connection and try again.</p>
          <button type="button" className="btn btn-secondary btn-compact" onClick={load}>
            Try again
          </button>
        </div>
      )}

      <SetupChecklist onActive={setSettingUp} />

      {/* While she's setting up, the checklist stands in for four zeros. */}
      {!(settingUp && screen.metrics.appointment_count === 0) && (
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-value revenue">{formatPrice(screen.metrics.revenue_pence)}</div>
          <div className="metric-label">booked today</div>
        </div>
        <div className="metric-card">
          <div className="metric-value">{screen.metrics.appointment_count}</div>
          <div className="metric-label">in the diary</div>
        </div>
        <div className="metric-card">
          <div className="metric-value">{screen.metrics.free_slots}</div>
          <div className="metric-label">{screen.metrics.free_slots === 1 ? 'slot free' : 'slots free'}</div>
        </div>
        <div className="metric-card">
          <div className="metric-value highlight">{screen.metrics.packages_due}</div>
          <div className="metric-label">
            {screen.metrics.packages_due === 1 ? 'package expiring' : 'packages expiring'}
          </div>
        </div>
      </div>
      )}

      {/* Off Pro the section is a locked hint (owner, 2026-10-24): each card's
          title and pounds, faded, with nothing to open or act on, whatever a
          cached Pro-era day carries. */}
      {priorities.length > 0 && coachLocked && (
        <ProLock hint={HINT_TODAY} lock="coach_action" className="priorities-section">
          <div className="section-header">
            <h2>Worth your attention</h2>
            <span className="priority-count">{priorities.length}</span>
          </div>
          <div className="priorities-list">
            {priorities.map((p, i) => (
              <div key={`${p.type}-${i}`} className="priority-card">
                <div className="priority-header">
                  <span className="priority-title">{p.title}</span>
                  {p.type !== 'coach' && p.type !== 'review' && <span className="priority-impact">{formatPrice(p.impact_pence)}</span>}
                </div>
              </div>
            ))}
          </div>
        </ProLock>
      )}

      {priorities.length > 0 && !coachLocked && (
        <div className="priorities-section">
          <div className="section-header">
            <h2>Worth your attention</h2>
            <span className="priority-count">{priorities.length}</span>
          </div>
          <div className="priorities-list">
            {priorities.map((p, i) => (
              <div key={`${p.type}-${p.client_id ?? ''}-${i}`} className="priority-card">
                <button
                  className="priority-header"
                  aria-expanded={openPriority === i}
                  onClick={() => setOpenPriority(openPriority === i ? null : i)}
                >
                  <span className="priority-title">{p.title}</span>
                  {p.type !== 'coach' && p.type !== 'review' && <span className="priority-impact">{formatPrice(p.impact_pence)}</span>}
                  <Icon name="chevron-down" size={18} className={`priority-caret ${openPriority === i ? 'open' : ''}`} />
                </button>
                {openPriority === i && (
                  <div className="priority-body">
                    <p className="priority-reason">{p.reason}</p>
                    <div className="priority-actions">
                      {p.type === 'rebooking' && p.phone && p.draft ? (
                        <a
                          className="btn btn-primary btn-compact"
                          href={whatsappLink(p.phone, p.draft)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => report(p, 'acted')}
                        >
                          {p.action}
                        </a>
                      ) : (
                        <button type="button" className="btn btn-primary btn-compact" onClick={() => openPriorityAction(p)}>
                          {p.action}
                        </button>
                      )}
                      <button type="button" className="btn btn-quiet btn-compact" onClick={() => snoozePriority(screen.date, p)}>
                        Snooze until tomorrow
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {screen.birthdays && screen.birthdays.length > 0 && (
        <div className="birthdays-section">
          <div className="section-header">
            <h2>Birthdays this week</h2>
          </div>
          <ul className="birthday-list">
            {screen.birthdays.map((b) => (
              <li key={b.client_id} className="birthday-row">
                <button
                  type="button"
                  className="birthday-row__who"
                  onClick={() => {
                    dispatch({ type: 'OPEN_CLIENT', payload: b.client_id })
                    dispatch({ type: 'SET_ACTIVE_TAB', payload: 'clients' })
                  }}
                >
                  <span className="birthday-row__name">{b.first_name}{b.last_name ? ` ${b.last_name}` : ''}</span>
                  <span className="birthday-row__when">{birthdayWhen(b.days_away, b.date)}</span>
                </button>
                {b.phone && (
                  <a
                    className="btn btn-secondary btn-compact"
                    href={whatsappLink(b.phone, birthdayMessage(b.first_name, name, b.days_away))}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Send wishes
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="today-section">
        <h2>Your day</h2>
        {!booked && (
          <p className="today-empty">{emptyDayLine(priorities, settingUp)}</p>
        )}
        {fullyBooked && <p className="today-empty">You’re fully booked today.</p>}
        <div className="appointments-list">
          {screen.appointments.map((appt) => (
            // Tapping opens the same full sheet as the Calendar (review 2026-10-14).
            <div key={appt.id} className="appointment-item">
              <div className="time-slot">{formatMinutes(appt.start_minute)}</div>
              <AppointmentRow
                appointment={appt}
                timezone={state.me?.professional.timezone}
                className="appointment-summary"
                showForm
                cardShown={cardShown}
                onOpen={() => dispatch({ type: 'OPEN_APPOINTMENT', payload: appt.id })}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
